import Service from './Service';
import OpenAIApi from './server/OpenAIApi';
import Server from './server';
import TreeCache from './cache/TreeCache';
import ScriptLoader from './ScriptLoader';
import checkContentType from '../utils/checkContentType';
import { ANSWER, CATEGORY, SUB_TREE, TEXT } from '../models/nodetypes';
import Constants from '../submodules/logictry_config/constants';

class OpenAIService extends Service {
  // Cache the text embeddings that have been received
  textEmbeddings = {};

  // Get Suggestions based on Website or HTML Provided
  getSuggestions = async (website, html) => {
    const request = website ? { action: 'GetWebsiteSuggestions', website } : { action: 'GetContentSuggestions', html };
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify(request), (response, complete) => {
      this.openAIState = {
        response,
        complete
      };
      this.suggestions = [];
      this.website = website;
      this.html = html;
      try {
        this.openAIState.response.split('{').forEach((cq, i) => {
          if (i === 0) return;
          try {
            const clarifyQuestion = cq.split('}')[0];
            const suggestion = clarifyQuestion && JSON.parse(`{${clarifyQuestion}}`) || '';
            if (suggestion) this.suggestions.push(suggestion);
          } catch {
            //
          }
        });
      } catch {
        //
      }
      this.emitStateUpdate();
    });
  }

  // Create Content by a type and description
  createContent = async (settings, team) => {
    const { type, description } = settings;
    if (this.openAIState && !this.openAIState.complete) return;
    this.openAIState = {
      type,
      description,
      response: '',
      complete: false,
    };
    this.emitStateUpdate();
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({ action: 'CreateContent', ...(settings || {})}), (response, complete) => {
      response = trimAndRemoveFORMATTER(response || '');
      this.openAIState = {
        type,
        description,
        response,
        complete
      };
      if (complete) {
        const newTreeText = response;
        const tree = TreeCache.createNew(newTreeText);
        if (team) tree.addTeam(team);
        TreeCache.createTree(tree);
        this.openAIState = null;
        this.emitStateUpdate();
      }
      this.emitStateUpdate();
    });
  }

  // update embeddings for Apps and Collections
  updateEmbedding = async (app, collection) => {
    let add;
    if (app) {
      add = await __getAllAppChunks(app);
    } else if (collection) {
      add = __getAllCollectionChunks(collection);
    }
    if (!add || add.error) return add.error || { error: 'NotFound' }

    // Send texts
    return OpenAIApi.create({
      action: 'UpdateEmbeddings',
      add,
      app: app?._id,
      collection: collection?._id
    });
  }
  askQuestion = async (question, app, report, collection, messages) => {
    let ids; // ID(s) of APPs to perform a semantic match
    let query = question; // The query or question being asked
    let context;
    let conversation;
    let error;

    // Allow asking questions on collection or apps individually
    if (collection) {
      // Include the collectionId and appId(s)
      ids = [collection._id, ...collection.apps.map(({ _id }) => _id)];
    } else if (app) {
      // Include the SubAppIds
      const subtreeNodes = app.root.__recursivelyGetAllNodesByTypes(app.root, [SUB_TREE]);
      if (report) {
        // This is the called if the chatbot replaces the report
        ({ conversation, error } = this.__getReportConversationAsynchronousFromInitialPrompt(app, messages?.slice(0, -1)));
      } else if (subtreeNodes && subtreeNodes.length > 0) {
        // Use existing vectors in the database for all sub trees
        ids = [app._id, ...subtreeNodes.map(({ text }) => text)];
      } else {
        // This is called if you want to use dynamic ebeddings
        const start = Math.max(1, messages.length - 2 - 20);
        const end = messages.length - 2;
        ({ context, conversation } = await this.__conversationAboutBodyOfText(app, question, messages.slice(start, end)));
      }
    }
    if (error) {
      this.openAIState = {
        response: '',
        complete: true,
      };
      this.emitStateUpdate();
    }

    // Construct prompt
    this.openAIState = {
      response: '',
      complete: false,
    };
    this.emitStateUpdate();
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({ action: 'AskQuestion', query, ids, context, conversation }), (response, complete) => {
      this.openAIState = {
        response,
        complete
      };
      this.emitStateUpdate();
    });
  }
  getReportAnswerSynchronousForStaticReport = async (tree) => {
    const result = await OpenAIApi.create({
      action: 'GetReportResult',
      _id: tree._id,
      answers: tree.getOpenAIPrompt()
    });
    return result;
  }

  // PRIVATE API CALLS
  // DO NOT CALL DIRECTLY BELOW
  __getReportConversationAsynchronousFromInitialPrompt = (tree, additionalConversation) => {
    const prompt = tree.getOpenAIPrompt();
    let conversation = [];
    Object.keys(prompt).forEach((assistant) => {
      conversation.push({ assistant });
      const user = prompt[assistant].join(' | ');
      conversation.push({ user });
    });
    if (additionalConversation) conversation = [...conversation, ...additionalConversation];

    // Reverse array
    conversation.reverse();
    const reversedArray = [...conversation];
    conversation = [];
    reversedArray.forEach((message) => {
      if (isTokenLengthTooLong(JSON.stringify(conversation))) return;
      conversation.push(message);
    });
    conversation.reverse();
    if (conversation.length === 0) {
      return { error: 'EmptyConversation' };
    }
    return { conversation };
  }

  __conversationAboutBodyOfText = async (tree, query, conversation) => {

    const texts = await __getAllAppChunks(tree);
    if (!texts) return;

    // Format texts and get embeddings
    let context = texts.filter((v) => v);
    const mustBreakTheDocumentIntoChunks = isTokenLengthTooLong(JSON.stringify(context));

    // Use embeddings to split up a document if the document link exceeds the max token limit
    if (mustBreakTheDocumentIntoChunks) {
      const allTextEmbeddingsFound = context.every((text) => this.textEmbeddings[text]);
      const queryWithConversation = conversation.length > 0 && `Based upon the previous questions and answers\n\n${conversation.map(({ question, answer }) => question && `## Question: ${question}` || answer && `## Answer: ${answer}`).join('\n\n')}\n\nAnswer the following question: ${query}` || query;
      const chunks = allTextEmbeddingsFound ? [queryWithConversation] : [queryWithConversation, ...context];
      const { embeddings, error } = await OpenAIApi.create({
        action: 'GetEmbeddings',
        chunks,
      });

      if (error || !embeddings || !embeddings[0]) {
        this.openAIState = {
          error,
          complete: true
        };
        this.emitStateUpdate();
        return;
      };

      // Get Embeddings
      const questionEmbedding = embeddings[0];
      if (embeddings.length > 1) {
        embeddings.slice(1).forEach((e, i) => {
          this.textEmbeddings[context[i]] = e;
        });
      }

      // Calculate similarities
      const similarities = [];
      context.forEach((formattedText, i) => {
        const embedding = this.textEmbeddings[formattedText];
        const similarity = calculateCosineSimilarity(questionEmbedding, embedding);
        similarities.push({ index: i, similarity, embedding, text: context[i] });
      });
      similarities.sort((a, b) => b.similarity - a.similarity);

      // Sort in ascending order of index
      context = [];
      similarities.forEach(({ text }) => {
        // Only check for previous similarity if i > 0
        if (isTokenLengthTooLong(JSON.stringify([...context, ...(conversation || [])]))) return;
        context.push(text);
      });
    }

    // Construct prompt
    this.openAIState = {
      response: '',
      complete: false,
    };

    // Send Async Request
    return { context, conversation };
  }
  sendRequest(url, method, body, cb) {
    this.xhttp = new XMLHttpRequest();
    this.xhttp.onreadystatechange = () => {
      if (this.xhttp.readyState === XMLHttpRequest.LOADING || this.xhttp.readyState === XMLHttpRequest.DONE) {
        cb(this.xhttp.responseText, this.xhttp.readyState === XMLHttpRequest.DONE);
      }
    };
    this.xhttp.open(method, url, true);
    this.xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) this.xhttp.setRequestHeader("csrf-token", csrfToken.content);
    this.xhttp.send(body);
  }
  cancelRequests() {
    this.xhttp?.abort();
  }
}

function trimAndRemoveFORMATTER(text) {
  return removeSurroundingQuotes(text.replaceAll('```json', '').replaceAll('```html', '').replaceAll('```plaintext', '').replaceAll('```', '').trim());
}
function removeSurroundingQuotes(str) {
  if (str.startsWith('"') && str.endsWith('"')) {
    return str.slice(1, -1);
  } else if (str.startsWith('"')) {
    return str.slice(1);
  } else if (str.endsWith('"')) {
    return str.slice(0, -1);
  }
  return str;
}
function calculateCosineSimilarity(vector1, vector2) {
  let dotProduct = 0;
  let normVector1 = 0;
  let normVector2 = 0;
  for (let i = 0; i < vector1.length; i += 1) {
    dotProduct += vector1[i] * vector2[i];
    normVector1 += vector1[i] * vector1[i];
    normVector2 += vector2[i] * vector2[i];
  }
  normVector1 = Math.sqrt(normVector1);
  normVector2 = Math.sqrt(normVector2);
  return dotProduct / (normVector1 * normVector2);
}

function isTokenLengthTooLong(text, maxTokens = 100000) {
  const charCount = text.length;
  const estimatedTokens = Math.ceil(charCount / 4);
  return estimatedTokens > maxTokens;
}
function extractCleanText(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Remove script/style/nav/etc
  const skipTags = ['SCRIPT', 'STYLE', 'NAV', 'FOOTER', 'HEADER', 'NOSCRIPT', 'ASIDE'];
  skipTags.forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => el.remove());
  });

  // Define meaningful block-level tags that warrant spacing
  const blockTags = new Set([
    'P', 'DIV', 'SECTION', 'ARTICLE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'LI', 'UL', 'OL', 'TABLE', 'TR',
    'BLOCKQUOTE', 'PRE', 'HR', 'BR'
  ]);

  let text = '';

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const content = node.textContent.replace(/\s+/g, ' ').trim();
      if (content) text += content + ' ';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (blockTags.has(node.tagName)) text += '\n';
      for (const child of node.childNodes) walk(child);
      if (blockTags.has(node.tagName)) text += '\n';
    }
  }

  walk(doc.body);

  // Collapse excess whitespace and trim
  return text.replace(/\s*\n\s*/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

async function __getAllAppChunks(tree) {
  if (tree.customSource && tree.customSource.url) {
    try {
      const { googleUrl, googleDriveUrl, youtubeUrl, loomUrl, airtableUrl, googleMapsUrl, canvaUrl, googleCalendarUrl, vimeoUrl, instagramEmbedCode, tiktokEmbedCode, isLogictryUrl } = checkContentType(tree.customSource.url);
      if (googleUrl || googleDriveUrl || youtubeUrl || loomUrl || airtableUrl || googleMapsUrl || canvaUrl || googleCalendarUrl || vimeoUrl || instagramEmbedCode || tiktokEmbedCode || isLogictryUrl) return;
      const filePath = `${Constants.logictryOrigin}/links?url=${tree.customSource.url}`;
      const response = await fetch(filePath);
      if (!response.ok) throw new Error('Failed to fetch file');
      const arrayBuffer = await response.arrayBuffer();
      const contentType = await Server.getContentType(filePath);
      if (contentType === 'application/pdf') {
        const pdfData = new Uint8Array(arrayBuffer);
        const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise;
        const numPages = pdf.numPages;
        let html = "";
        async function extractTextWithHeaders(pageNumber) {
          if (pageNumber > numPages) return;
          const page = await pdf.getPage(pageNumber);
          const textContent = await page.getTextContent();
          textContent.items.forEach(function (textItem) {
            html += `${textItem.str}${textItem.hasEOL ? '\n' : ''}`;
          });
          await extractTextWithHeaders(pageNumber + 1);
        }
        await extractTextWithHeaders(1);
        return html && [html];
      // } else if (contentType === 'application/pdf') {
      //   const { value } = await window.mammoth.convertToHtml({ arrayBuffer });
      } else {
        //
      }
    } catch {
      //
    }
  } else {
    // const categoryNodes = app.root.__recursivelyGetAllNodesByTypes(app.root, [CATEGORY]);
    const textNodes = tree.root.__recursivelyGetAllNodesByTypes(tree.root, [TEXT, ANSWER]);
    const tooLong = [];
    textNodes.forEach(({ text }) => {
      if (isTokenLengthTooLong(text)) {
        tooLong.push(text);
      }
    });
    if (tooLong.length > 0) return { error: 'TextTooLong' };

    //
    let tableOfContents = ['Table of Contents'];
    const textNodesWithTOC = [];

    const tocCategoryAlreadyIncluded = new Set();
    let tocIndex = [];

    if (textNodes) {
      textNodes.forEach(({ text, parents }) => {
        let textNodeWithTOC = [];
        let level = 0;
        parents.forEach((parent) => {
          if (parent.isOneOfTypes([CATEGORY])) {
            const tocString = tocIndex.slice(0, level + 1).join('.') + ' ' + extractCleanText(parent.text);
            textNodeWithTOC.push(tocString);
            if (!tocCategoryAlreadyIncluded.has(parent)) {
              tocCategoryAlreadyIncluded.add(parent);
              tocIndex[level] = (tocIndex[level] || 0) + 1;
              tocIndex = tocIndex.slice(0, level + 1);
              if (!tableOfContents.includes(tocString)) tableOfContents.push(tocString);
            }
            level += 1;
          }
        });

        // Final content line
        textNodeWithTOC.push(extractCleanText(text));
        textNodesWithTOC.push(textNodeWithTOC.join('\n'));
      });
    }
    return [tableOfContents.join('\n'), ...textNodesWithTOC].filter((v) => v);
  }
}
function __getAllCollectionChunks() {

}


const singleton = new OpenAIService();
ScriptLoader.onStateUpdate(() => {
  if (!ScriptLoader.isLoaded('https://logictrystatic.s3.amazonaws.com/js/pdf.es5.js')) return;
  if (!ScriptLoader.isLoaded('https://logictrystatic.s3.amazonaws.com/js/mammoth.browser.min.js')) return;
  if (!window.pdfjsLib) return;
  window.pdfjsLib.disableWorker = true;
  window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://logictrystatic.s3.amazonaws.com/js/pdf.worker.min.js';
});
ScriptLoader.load('https://logictrystatic.s3.amazonaws.com/js/pdf.es5.js');
ScriptLoader.load('https://logictrystatic.s3.amazonaws.com/js/mammoth.browser.min.js');
export default singleton;
